import { useState, useEffect, useRef, useCallback } from 'react';

const GOOGLE_MAPS_SRC =
	'https://maps.googleapis.com/maps/api/js?jsapiRedirect=true&language=en&client=gme-coxenterprises&channel=ddc-web-platform&libraries=places';

// Module-level variables to ensure single script load
let isScriptLoading = false;
let isScriptLoaded = false;

/**
 * AutocompleteSessionToken Management:
 *
 * 1. Billing Optimization:
 *    - Without a session token, every keystroke triggers a separately billed API call
 *    - With a session token, multiple keystrokes are grouped into one billable session
 *
 * 2. Session Lifecycle:
 *    - Create: When user starts typing (input length > 3)
 *    - Reuse: During continuous typing/refinement of same search
 *    - Reset: When user clears input or selects a place
 *
 * 3. Cost Example:
 *    Without Token: "New York" = 8 separate billing events
 *    With Token: "New York" = 1 billable session
 *
 * 4. Negative Impacts of Token Reuse:
 *    - Violates Google's session semantics (one session = one search flow)
 *    - May lead to incorrect billing aggregation
 *    - Can cause unexpected API behavior or throttling
 *    - Example: Using same token for "New York" then "Chicago" search
 *      could be flagged as abuse or lead to invalid session metrics
 *
 * @param {string} inputValue - Current search input value
 * @returns {Object} API status and session token
 */
export const useGoogleApiSession = (inputValue) => {
	const [isApiReady, setIsApiReady] = useState(false);
	const sessionTokenRef = useRef(null);

	// Handle script loading
	useEffect(() => {
		if (window.google?.maps?.places) {
			isScriptLoaded = true;
			setIsApiReady(true);
			return null;
		}
		// Script is being loaded by another instance, just poll
		if (isScriptLoading) {
			const poll = setInterval(() => {
				if (window.google?.maps?.places) {
					clearInterval(poll);
					isScriptLoaded = true;
					setIsApiReady(true);
				}
			}, 100);
			return () => {
				clearInterval(poll);
			};
		}

		// Check if script is already in the header
		let script = document.querySelector(
			'script[src*="maps.googleapis.com/maps/api/js"]'
		);
		if (!script) {
			// Create and load the script
			isScriptLoading = true;
			script = document.createElement('script');
			script.src = GOOGLE_MAPS_SRC;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		}

		script.onload = () => {
			isScriptLoaded = true;
			isScriptLoading = false;
			setIsApiReady(true);
		};

		script.onerror = () => {
			isScriptLoading = false;
			isScriptLoaded = false;
			setIsApiReady(false);
		};

		// Cleanup: Reset loading flag if component unmounts before load completes
		return () => {
			if (!isScriptLoaded) {
				isScriptLoading = false;
			}
			return null;
		};
	}, []);

	// Reset function
	const resetSession = useCallback(() => {
		sessionTokenRef.current = null;
	}, []);

	// Handle session token lifecycle
	useEffect(() => {
		if (!isApiReady) {
			return null;
		}

		// End session if input is empty or too short
		if (!inputValue || inputValue.trim().length < 3) {
			resetSession();
			return null;
		}

		// Create new session token if needed
		if (!sessionTokenRef.current && window.google?.maps?.places) {
			sessionTokenRef.current =
				new window.google.maps.places.AutocompleteSessionToken();
		}
		return null;
	}, [inputValue, isApiReady, resetSession]);

	return {
		isApiReady,
		sessionToken: sessionTokenRef.current,
		resetSession
	};
};
